import { faPlus } from "@fortawesome/pro-light-svg-icons";
import { Box, Typography, TableBody as MuiTableBody, TableRow, TableCell } from "@material-ui/core";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";
import { savePhoneNumberThunk } from "actions/businesses/BusinessesActions";
import { Button, Table } from "components/UI";
import React, { FunctionComponent, useState } from "react";
import { useDispatch } from "react-redux";
import AddPhoneNumberPopUp from "./AddPhoneNumberPopUp";
import TableBody from "./TablePhoneNumbers/TableBody";
import TableHeader from "./TablePhoneNumbers/TableHeader";

interface Props {
  twilioPhoneNumber?: string;
  marketplaceId: number;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
    },
    buttonRow: {
      display: "flex",
      justifyContent: "flex-end",
      [theme.breakpoints.down("sm")]: {
        paddingTop: "16px",
        paddingBottom: "16px",
        paddingRight: "41px",
      },
      [theme.breakpoints.only("md")]: {
        paddingTop: "20px",
        paddingBottom: "20px",
        paddingRight: "65px",
      },
      [theme.breakpoints.only("lg")]: {
        paddingTop: "24px",
        paddingBottom: "24px",
        paddingRight: "87px",
      },
      [theme.breakpoints.only("xl")]: {
        paddingTop: "28px",
        paddingBottom: "28px",
        paddingRight: "132px",
      },
    },
    addButton: {
      "& .MuiButton-startIcon": {
        [theme.breakpoints.down("sm")]: {
          marginRight: "1px",
        },
        [theme.breakpoints.only("md")]: {
          marginRight: "1px",
        },
        [theme.breakpoints.only("lg")]: {
          marginRight: "2px",
        },
        [theme.breakpoints.only("xl")]: {
          marginRight: "4px",
        },
      },
      "& .MuiButton-label": {
        [theme.breakpoints.down("sm")]: {
          fontSize: "5px",
        },
        [theme.breakpoints.only("md")]: {
          fontSize: "8px",
        },
        [theme.breakpoints.only("lg")]: {
          fontSize: "11px",
        },
        [theme.breakpoints.only("xl")]: {
          fontSize: "16px",
        },
      },
      [theme.breakpoints.down("sm")]: {
        width: "70px",
        height: "15px",
      },
      [theme.breakpoints.only("md")]: {
        width: "112px",
        height: "24px",
      },
      [theme.breakpoints.only("lg")]: {
        width: "149px",
        height: "32px",
      },
      [theme.breakpoints.only("xl")]: {
        width: "225px",
        height: "49px",
      },
    },
    tableWrapper: {
      width: "100%",
      [theme.breakpoints.down("sm")]: {
        overflowX: "auto",
      },
    },
    emptyStateContainer: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      padding: "40px",
      [theme.breakpoints.down("sm")]: {
        padding: "24px 16px",
      },
    },
    emptyStateImage: {
      width: "370px",
      marginBottom: "24px",
      [theme.breakpoints.down("sm")]: {
        width: "200px",
        marginBottom: "16px",
      },
    },
    emptyStateTitle: {
      fontSize: "22px",
      fontWeight: 600,
      color: "#222222",
      marginBottom: "8px",
      [theme.breakpoints.down("sm")]: {
        fontSize: "16px",
        marginBottom: "4px",
      },
    },
    emptyStateDescription: {
      fontSize: "16px",
      color: "#222222",
      [theme.breakpoints.down("sm")]: {
        fontSize: "12px",
      },
    },
  })
);

const parseApiError = (error: unknown): string => {
  const err = error as any;

  if (err?.response?.data?.twilio_phone_number) {
    // Handle format: {"twilio_phone_number":["is already in use by another marketplace"]}
    return "Phone number " + err.response.data.twilio_phone_number.join(", ");
  }

  if (err?.response?.data?.errors?.twilio_phone_number) {
    // Handle format: {"errors": {"twilio_phone_number": ["message"]}}
    return "Phone number " + err.response.data.errors.twilio_phone_number.join(", ");
  }

  if (err?.response?.data?.message) {
    return err.response.data.message.replace(/twilio_phone_number/gi, "Phone number");
  }

  if (err?.message) {
    return err.message;
  }

  return "Error saving phone number";
};

export const BusinessPhoneNumbers: FunctionComponent<Props> = (props) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [showAddModal, setShowAddModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const hasPhoneNumber = !!props.twilioPhoneNumber;

  const handleAddClick = () => {
    setShowAddModal(true);
    setApiError(null);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setApiError(null);
  };

  const handleSavePhoneNumber = async (phoneNumber: string) => {
    setSaving(true);
    setApiError(null);
    try {
      await dispatch(savePhoneNumberThunk(props.marketplaceId, phoneNumber));
      setShowAddModal(false);
    } catch (error) {
      setApiError(parseApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const emptyStateComponent = (
    <MuiTableBody>
      <TableRow>
        <TableCell colSpan={2} style={{ borderBottom: "none" }}>
          <Box className={classes.emptyStateContainer}>
            <img
              src="/images/not-found.svg"
              alt="No phone numbers"
              className={classes.emptyStateImage}
            />
            <Typography className={classes.emptyStateTitle}>
              No phone numbers yet
            </Typography>
            <Typography className={classes.emptyStateDescription}>
              Add a phone number for this client.
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
    </MuiTableBody>
  );

  return (
    <Box className={classes.container}>
      {!hasPhoneNumber && (
        <Box className={classes.buttonRow}>
          <Button
            startIcon={faPlus}
            label="Add Phone Number"
            onClick={handleAddClick}
            variant="primary"
            size="medium"
            color="orange"
            className={classes.addButton}
          />
        </Box>
      )}
      <Box className={classes.tableWrapper}>
        <Table
          head={<TableHeader />}
          body={hasPhoneNumber ? <TableBody twilioPhoneNumber={props.twilioPhoneNumber} /> : emptyStateComponent}
        />
      </Box>
      {showAddModal && (
        <AddPhoneNumberPopUp
          onClose={handleCloseModal}
          onSave={handleSavePhoneNumber}
          saving={saving}
          apiError={apiError}
        />
      )}
    </Box>
  );
};

export default BusinessPhoneNumbers;
